/** The Save, Rename and Delete dialogs (2.K deliverables 10 and 11), on the kit's `Dialog`. */
import type { SavedViewRequestLayout } from '@codex/contracts';
import { Button, Checkbox, Dialog, SegmentedControl, TextField } from '@codex/ui';
import { useEffect, useState } from 'react';
import { saveIssueCopy } from './model/copy.js';

export const LAYOUT_OPTIONS = [
  { value: 'table', label: 'Table' },
  { value: 'list', label: 'List' },
] as const;

const NAME_MAX = 200;

export interface SaveInput {
  readonly name: string;
  readonly layout: SavedViewRequestLayout;
  readonly pin: boolean;
}

export function SaveViewDialog({
  open,
  initialName,
  initialLayout,
  busy,
  error,
  onSave,
  onClose,
}: {
  open: boolean;
  initialName: string;
  initialLayout: SavedViewRequestLayout;
  busy: boolean;
  error: string | null;
  onSave: (input: SaveInput) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(initialName);
  const [layout, setLayout] = useState<SavedViewRequestLayout>(initialLayout);
  const [pin, setPin] = useState(false);
  const [local, setLocal] = useState<string | null>(null);

  // A dialog opened again starts from the current page, not from the last attempt.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only `open` restarts the draft
  useEffect(() => {
    if (!open) return;
    setName(initialName);
    setLayout(initialLayout);
    setPin(false);
    setLocal(null);
  }, [open]);

  const submit = () => {
    if (busy) return;
    if (name.trim() === '') {
      setLocal(saveIssueCopy('invalid_name'));
      return;
    }
    onSave({ name: name.trim(), layout, pin });
  };
  const shown = local ?? error;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Save view"
      busy={busy}
      actions={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" busy={busy} onClick={submit}>
            Save
          </Button>
        </>
      }
    >
      <form
        className="cx-views__form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <TextField
          label="Name"
          value={name}
          maxLength={NAME_MAX}
          required
          onChange={(event) => {
            setName(event.target.value);
            setLocal(null);
          }}
        />
        <SegmentedControl
          label="Layout"
          options={LAYOUT_OPTIONS}
          value={layout}
          onChange={setLayout}
        />
        <Checkbox label="Pin to sidebar" checked={pin} onChange={setPin} />
        {shown !== null && <p role="alert">{shown}</p>}
      </form>
    </Dialog>
  );
}

export function RenameDialog({
  open,
  initialName,
  busy,
  error,
  onRename,
  onClose,
}: {
  open: boolean;
  initialName: string;
  busy: boolean;
  error: string | null;
  onRename: (name: string) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(initialName);
  const [local, setLocal] = useState<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: only `open` restarts the draft
  useEffect(() => {
    if (!open) return;
    setName(initialName);
    setLocal(null);
  }, [open]);
  const submit = () => {
    if (busy) return;
    if (name.trim() === '') {
      setLocal(saveIssueCopy('invalid_name'));
      return;
    }
    onRename(name.trim());
  };
  const shown = local ?? error;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Rename view"
      busy={busy}
      actions={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" busy={busy} onClick={submit}>
            Rename
          </Button>
        </>
      }
    >
      <form
        className="cx-views__form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <TextField
          label="Name"
          value={name}
          maxLength={NAME_MAX}
          required
          onChange={(event) => {
            setName(event.target.value);
            setLocal(null);
          }}
        />
        {shown !== null && <p role="alert">{shown}</p>}
      </form>
    </Dialog>
  );
}

export const DELETE_COPY =
  'Delete this view? Your pages are not touched; only the saved view and its sidebar pin are removed.';

export function DeleteDialog({
  open,
  busy,
  onDelete,
  onClose,
}: {
  open: boolean;
  busy: boolean;
  onDelete: () => void;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      variant="alert"
      title="Delete view"
      description={DELETE_COPY}
      busy={busy}
      actions={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="danger" busy={busy} onClick={onDelete}>
            Delete
          </Button>
        </>
      }
    />
  );
}
